import { bookings, fields, preBookedSlots } from './mockData';
import { Booking } from '../types';
import { notifyLocalDataChanged, syncBookingsToSupabase } from './supabaseSync';
import {
  createBookingApprovedNotification,
  createBookingPaymentProofNotification,
  createBookingPaymentRejectedNotification,
  createBookingPaidNotifications,
  createBookingRequestNotification,
  dispatchBookingConfirmationMessages,
  markWaitlistConvertedByBooking,
  notifyWaitlistForOpenedSlot,
  registerBookingReminders,
} from './engagementStore';
import { getOwnerProfileById, getSession } from './authStore';
import { logAuditEvent } from './auditStore';

const EXTRA_BOOKINGS_KEY = 'halisaha_extra_bookings';
const BOOKING_LOCKS_KEY = 'halisaha_booking_locks';
const LOCK_TTL_MS = 15000;

function readExtraBookings(): Booking[] {
  try {
    const raw = localStorage.getItem(EXTRA_BOOKINGS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Booking[];
  } catch {
    return [];
  }
}

function writeExtraBookings(list: Booking[]) {
  localStorage.setItem(EXTRA_BOOKINGS_KEY, JSON.stringify(list));
  syncBookingsToSupabase();
  notifyLocalDataChanged();
}

function readLocks(): Record<string, number> {
  try {
    const raw = localStorage.getItem(BOOKING_LOCKS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, number>;
  } catch {
    return {};
  }
}

function writeLocks(value: Record<string, number>) {
  localStorage.setItem(BOOKING_LOCKS_KEY, JSON.stringify(value));
}

function buildSlotKeys(fieldId: string, date: string, startHour: number, duration: number): string[] {
  const keys: string[] = [];
  const step = 1;
  const end = startHour + duration;
  for (let h = startHour; h < end - 0.001; h += step) {
    keys.push(`${fieldId}:${date}:${Number(h.toFixed(2))}`);
  }
  return keys;
}

function acquireSlotLocks(keys: string[]): boolean {
  const now = Date.now();
  const locks = readLocks();
  const cleaned: Record<string, number> = {};
  Object.entries(locks).forEach(([key, until]) => {
    if (until > now) cleaned[key] = until;
  });
  const hasLocked = keys.some((key) => (cleaned[key] || 0) > now);
  if (hasLocked) {
    writeLocks(cleaned);
    return false;
  }
  const lockUntil = now + LOCK_TTL_MS;
  keys.forEach((key) => {
    cleaned[key] = lockUntil;
  });
  writeLocks(cleaned);
  return true;
}

function releaseSlotLocks(keys: string[]) {
  const locks = readLocks();
  keys.forEach((key) => delete locks[key]);
  writeLocks(locks);
}

function lockBookedSlots(fieldId: string, date: string, startHour: number, duration: number, teamName: string, stepMinutes: 60) {
  const stepHours = stepMinutes / 60;
  const steps = Math.max(1, Math.round(duration / stepHours));

  for (let i = 0; i < steps; i += 1) {
    const hour = startHour + i * stepHours;
    const exists = preBookedSlots.some(
      (slot) => slot.fieldId === fieldId && slot.date === date && slot.hour === hour
    );
    if (!exists) {
      preBookedSlots.push({ fieldId, date, hour, isBooked: true, bookedBy: teamName });
    }
  }
}

export function rebuildPreBookedSlotsFromBookings() {
  const ownerBlocked = preBookedSlots.filter((slot) => slot.bookedBy === OWNER_BLOCK_LABEL);
  preBookedSlots.splice(0, preBookedSlots.length, ...ownerBlocked);

  const all = getAllBookings().filter((item) => item.status !== 'cancelled');
  all.forEach((item) => {
    lockBookedSlots(item.fieldId, item.date, item.hour, item.duration, item.teamName, 60);
  });
}

function unlockBookedSlots(fieldId: string, date: string, startHour: number, duration: number) {
  const endHour = startHour + duration;
  for (let i = preBookedSlots.length - 1; i >= 0; i -= 1) {
    const slot = preBookedSlots[i];
    if (slot.fieldId !== fieldId || slot.date !== date) continue;
    if (slot.hour + 0.001 < startHour) continue;
    if (slot.hour >= endHour - 0.001) continue;
    preBookedSlots.splice(i, 1);
  }
}

function notifyWaitlistForReleasedRange(booking: Pick<Booking, 'fieldId' | 'fieldName' | 'date' | 'hour' | 'duration'>) {
  const endHour = booking.hour + booking.duration;
  for (let h = booking.hour; h < endHour - 0.001; h += 0.5) {
    notifyWaitlistForOpenedSlot({
      fieldId: booking.fieldId,
      fieldName: booking.fieldName,
      date: booking.date,
      hour: Number(h.toFixed(2)),
    });
  }
}

export function getAllBookings(): Booking[] {
  return [...readExtraBookings(), ...bookings];
}

export function getBookingById(bookingId: string): Booking | undefined {
  return getAllBookings().find((item) => item.id === bookingId);
}

function isHourLockedInPrebook(fieldId: string, date: string, hour: number): boolean {
  return preBookedSlots.some(
    (slot) =>
      slot.fieldId === fieldId &&
      slot.date === date &&
      Math.abs(slot.hour - hour) < 0.001 &&
      slot.isBooked
  );
}

export function createBooking(input: Omit<Booking, 'id' | 'createdAt' | 'status'>): { ok: true; booking: Booking } | { ok: false; error: string } {
  const lockKeys = buildSlotKeys(input.fieldId, input.date, input.hour, input.duration);
  if (!acquireSlotLocks(lockKeys)) {
    return { ok: false, error: 'Seçilen slot için işlem devam ediyor. Lütfen birkaç saniye sonra tekrar deneyin.' };
  }
  try {
  const all = getAllBookings();
  const hasConflict = all.some((item) => {
    if (item.fieldId !== input.fieldId) return false;
    if (item.date !== input.date) return false;
    if (item.status === 'cancelled') return false;
    const itemEnd = item.hour + item.duration;
    const inputEnd = input.hour + input.duration;
    return input.hour < itemEnd && inputEnd > item.hour;
  });

  if (hasConflict) {
    return { ok: false, error: 'Seçilen saat aralığı artık dolu görünüyor. Lütfen tekrar seçim yapın.' };
  }

  const end = input.hour + input.duration;
  for (let h = input.hour; h < end - 0.001; h += 1) {
    const hour = Number(h.toFixed(2));
    if (isHourLockedInPrebook(input.fieldId, input.date, hour)) {
      return { ok: false, error: 'Seçilen saat aralığı müsait değil. Lütfen başka saat seçin.' };
    }
  }

  const booking: Booking = {
    ...input,
    id: `book-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    status: 'pending_owner_approval',
    createdAt: new Date().toISOString(),
  };

  const extra = readExtraBookings();
  writeExtraBookings([booking, ...extra]);
  lockBookedSlots(booking.fieldId, booking.date, booking.hour, booking.duration, booking.teamName, 60);
  const field = fields.find((item) => item.id === booking.fieldId);
  const owner = field ? getOwnerProfileById(field.ownerId) : undefined;
  createBookingRequestNotification({
    referenceCode: booking.id.toUpperCase(),
    fieldId: booking.fieldId,
    fieldName: booking.fieldName,
    date: booking.date,
    startTime: formatHourValue(booking.hour),
    endTime: formatHourValue(booking.hour + booking.duration),
    ownerId: field?.ownerId,
    ownerPhone: owner?.phone,
  });
  logAuditEvent({
    session: getSession(),
    action: 'booking.request.created',
    targetType: 'booking',
    targetId: booking.id,
    meta: {
      fieldId: booking.fieldId,
      date: booking.date,
      hour: booking.hour,
      duration: booking.duration,
      status: booking.status,
    },
  });
  return { ok: true, booking };
  } finally {
    releaseSlotLocks(lockKeys);
  }
}

function formatHourValue(value: number): string {
  const totalMinutes = Math.round(value * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function approveBookingByOwner(bookingId: string, ownerId: string): { ok: boolean; booking?: Booking } {
  const extra = readExtraBookings();
  const index = extra.findIndex((item) => item.id === bookingId);
  if (index < 0) return { ok: false };
  const booking = extra[index];
  if (booking.status !== 'pending_owner_approval') return { ok: false };
  const field = fields.find((item) => item.id === booking.fieldId);
  if (!field || field.ownerId !== ownerId) return { ok: false };
  extra[index] = { ...booking, status: 'awaiting_payment' };
  writeExtraBookings(extra);
  createBookingApprovedNotification({
    referenceCode: booking.id.toUpperCase(),
    fieldId: booking.fieldId,
    fieldName: booking.fieldName,
    date: booking.date,
    startTime: formatHourValue(booking.hour),
    endTime: formatHourValue(booking.hour + booking.duration),
    customerId: booking.customerId,
    customerPhone: booking.contactPhone,
  });
  logAuditEvent({
    session: getSession(),
    action: 'booking.request.approved_by_owner',
    targetType: 'booking',
    targetId: booking.id,
    meta: { ownerId, previousStatus: booking.status, nextStatus: 'awaiting_payment' },
  });
  return { ok: true, booking: extra[index] };
}

export function markBookingPaidByCustomer(bookingId: string, customerId?: string): { ok: boolean; booking?: Booking } {
  const extra = readExtraBookings();
  const index = extra.findIndex((item) => item.id === bookingId);
  if (index < 0) return { ok: false };
  const booking = extra[index];
  if (booking.status !== 'awaiting_payment') return { ok: false };
  if (customerId && booking.customerId && booking.customerId !== customerId) return { ok: false };

  extra[index] = { ...booking, status: 'confirmed' };
  writeExtraBookings(extra);

  registerBookingReminders(extra[index]);
  markWaitlistConvertedByBooking(extra[index]);

  const field = fields.find((item) => item.id === booking.fieldId);
  const owner = field ? getOwnerProfileById(field.ownerId) : undefined;
  dispatchBookingConfirmationMessages({
    booking: extra[index],
    ownerId: field?.ownerId,
    ownerPhone: owner?.phone,
    ownerName: owner?.fullName,
  });
  createBookingPaidNotifications({
    referenceCode: booking.id.toUpperCase(),
    fieldId: booking.fieldId,
    fieldName: booking.fieldName,
    date: booking.date,
    startTime: formatHourValue(booking.hour),
    endTime: formatHourValue(booking.hour + booking.duration),
    customerId: booking.customerId,
    customerPhone: booking.contactPhone,
    ownerId: field?.ownerId,
    ownerPhone: owner?.phone,
  });
  logAuditEvent({
    session: getSession(),
    action: 'booking.payment.marked',
    targetType: 'booking',
    targetId: booking.id,
    meta: { previousStatus: booking.status, nextStatus: 'confirmed' },
  });

  return { ok: true, booking: extra[index] };
}

export function submitBookingPaymentProof(
  bookingId: string,
  input: {
    customerId?: string;
    proofUrl: string;
    proofFileName?: string;
    proofNote?: string;
  }
): { ok: boolean; booking?: Booking; error?: string } {
  const extra = readExtraBookings();
  const index = extra.findIndex((item) => item.id === bookingId);
  if (index < 0) return { ok: false, error: 'Rezervasyon bulunamadı.' };

  const booking = extra[index];
  if (booking.status !== 'awaiting_payment') {
    return { ok: false, error: 'Bu rezervasyon şu an ödeme kanıtı kabul etmiyor.' };
  }
  if (input.customerId && booking.customerId && booking.customerId !== input.customerId) {
    return { ok: false, error: 'Bu işlem için yetkiniz yok.' };
  }
  if (!input.proofUrl.trim()) {
    return { ok: false, error: 'Dekont bağlantısı boş olamaz.' };
  }

  const next: Booking = {
    ...booking,
    status: 'payment_review',
    paymentProofUrl: input.proofUrl.trim(),
    paymentProofFileName: input.proofFileName?.trim() || undefined,
    paymentProofNote: input.proofNote?.trim() || undefined,
    paymentProofSubmittedAt: new Date().toISOString(),
    paymentProofRejectedReason: undefined,
    paymentReviewedAt: undefined,
  };

  extra[index] = next;
  writeExtraBookings(extra);

  const field = fields.find((item) => item.id === booking.fieldId);
  const owner = field ? getOwnerProfileById(field.ownerId) : undefined;
  createBookingPaymentProofNotification({
    referenceCode: booking.id.toUpperCase(),
    fieldId: booking.fieldId,
    fieldName: booking.fieldName,
    date: booking.date,
    startTime: formatHourValue(booking.hour),
    endTime: formatHourValue(booking.hour + booking.duration),
    ownerId: field?.ownerId,
    ownerPhone: owner?.phone,
  });
  logAuditEvent({
    session: getSession(),
    action: 'booking.payment_proof.submitted',
    targetType: 'booking',
    targetId: booking.id,
    meta: { previousStatus: booking.status, nextStatus: 'payment_review' },
  });

  return { ok: true, booking: next };
}

export function approveBookingPaymentByOwner(
  bookingId: string,
  ownerId: string
): { ok: boolean; booking?: Booking; error?: string } {
  const extra = readExtraBookings();
  const index = extra.findIndex((item) => item.id === bookingId);
  if (index < 0) return { ok: false, error: 'Rezervasyon bulunamadı.' };
  const booking = extra[index];
  if (booking.status !== 'payment_review') {
    return { ok: false, error: 'Rezervasyon ödeme kontrol aşamasında değil.' };
  }
  const field = fields.find((item) => item.id === booking.fieldId);
  if (!field || field.ownerId !== ownerId) {
    return { ok: false, error: 'Bu işlem için yetkiniz yok.' };
  }

  const next: Booking = {
    ...booking,
    status: 'confirmed',
    paymentReviewedAt: new Date().toISOString(),
    paymentProofRejectedReason: undefined,
  };
  extra[index] = next;
  writeExtraBookings(extra);

  registerBookingReminders(next);
  markWaitlistConvertedByBooking(next);

  const owner = getOwnerProfileById(ownerId);
  dispatchBookingConfirmationMessages({
    booking: next,
    ownerId: field.ownerId,
    ownerPhone: owner?.phone,
    ownerName: owner?.fullName,
  });
  createBookingPaidNotifications({
    referenceCode: booking.id.toUpperCase(),
    fieldId: booking.fieldId,
    fieldName: booking.fieldName,
    date: booking.date,
    startTime: formatHourValue(booking.hour),
    endTime: formatHourValue(booking.hour + booking.duration),
    customerId: booking.customerId,
    customerPhone: booking.contactPhone,
    ownerId: field.ownerId,
    ownerPhone: owner?.phone,
  });
  logAuditEvent({
    session: getSession(),
    action: 'booking.payment.approved_by_owner',
    targetType: 'booking',
    targetId: booking.id,
    meta: { previousStatus: booking.status, nextStatus: 'confirmed', ownerId },
  });
  return { ok: true, booking: next };
}

export function rejectBookingPaymentByOwner(
  bookingId: string,
  ownerId: string,
  reason?: string
): { ok: boolean; booking?: Booking; error?: string } {
  const extra = readExtraBookings();
  const index = extra.findIndex((item) => item.id === bookingId);
  if (index < 0) return { ok: false, error: 'Rezervasyon bulunamadı.' };
  const booking = extra[index];
  if (booking.status !== 'payment_review') {
    return { ok: false, error: 'Rezervasyon ödeme kontrol aşamasında değil.' };
  }
  const field = fields.find((item) => item.id === booking.fieldId);
  if (!field || field.ownerId !== ownerId) {
    return { ok: false, error: 'Bu işlem için yetkiniz yok.' };
  }

  const next: Booking = {
    ...booking,
    status: 'awaiting_payment',
    paymentProofRejectedReason: reason?.trim() || 'Dekont doğrulanamadı.',
    paymentReviewedAt: new Date().toISOString(),
  };
  extra[index] = next;
  writeExtraBookings(extra);

  createBookingPaymentRejectedNotification({
    referenceCode: booking.id.toUpperCase(),
    fieldId: booking.fieldId,
    fieldName: booking.fieldName,
    date: booking.date,
    startTime: formatHourValue(booking.hour),
    endTime: formatHourValue(booking.hour + booking.duration),
    customerId: booking.customerId,
    customerPhone: booking.contactPhone,
    reason: next.paymentProofRejectedReason,
  });
  logAuditEvent({
    session: getSession(),
    action: 'booking.payment.rejected_by_owner',
    targetType: 'booking',
    targetId: booking.id,
    meta: {
      previousStatus: booking.status,
      nextStatus: 'awaiting_payment',
      ownerId,
      reason: next.paymentProofRejectedReason,
    },
  });
  return { ok: true, booking: next };
}

export function cancelBookingById(bookingId: string): boolean {
  return cancelBookingWithResult(bookingId).ok;
}

export function cancelBookingWithResult(bookingId: string): { ok: boolean; booking?: Booking } {
  const staticBooking = bookings.find((item) => item.id === bookingId);
  if (staticBooking) {
    if (staticBooking.status === 'cancelled') return { ok: false };
    staticBooking.status = 'cancelled';
    unlockBookedSlots(staticBooking.fieldId, staticBooking.date, staticBooking.hour, staticBooking.duration);
    notifyWaitlistForReleasedRange(staticBooking);
    logAuditEvent({
      session: getSession(),
      action: 'booking.cancelled',
      targetType: 'booking',
      targetId: staticBooking.id,
      meta: { fieldId: staticBooking.fieldId },
    });
    return { ok: true, booking: staticBooking };
  }

  const extra = readExtraBookings();
  const index = extra.findIndex((item) => item.id === bookingId);
  if (index < 0) return { ok: false };
  if (extra[index].status === 'cancelled') return { ok: false };
  extra[index] = { ...extra[index], status: 'cancelled' };
  writeExtraBookings(extra);
  unlockBookedSlots(extra[index].fieldId, extra[index].date, extra[index].hour, extra[index].duration);
  notifyWaitlistForReleasedRange(extra[index]);
  logAuditEvent({
    session: getSession(),
    action: 'booking.cancelled',
    targetType: 'booking',
    targetId: extra[index].id,
    meta: { fieldId: extra[index].fieldId },
  });
  return { ok: true, booking: extra[index] };
}

const OWNER_BLOCK_LABEL = 'SS Blok';

rebuildPreBookedSlotsFromBookings();

export function isSlotBlockedByOwner(fieldId: string, date: string, hour: number): boolean {
  return preBookedSlots.some(
    (slot) =>
      slot.fieldId === fieldId &&
      slot.date === date &&
      Math.abs(slot.hour - hour) < 0.001 &&
      slot.isBooked &&
      slot.bookedBy === OWNER_BLOCK_LABEL
  );
}

export function toggleOwnerSlotBlock(fieldId: string, date: string, hour: number): { ok: boolean; blocked: boolean } {
  const blockIndex = preBookedSlots.findIndex(
    (slot) =>
      slot.fieldId === fieldId &&
      slot.date === date &&
      Math.abs(slot.hour - hour) < 0.001 &&
      slot.isBooked &&
      slot.bookedBy === OWNER_BLOCK_LABEL
  );

  if (blockIndex >= 0) {
    preBookedSlots.splice(blockIndex, 1);
    return { ok: true, blocked: false };
  }

  const hasRealBooking = getAllBookings().some(
    (item) =>
      item.fieldId === fieldId &&
      item.date === date &&
      item.status !== 'cancelled' &&
      hour >= item.hour - 0.001 &&
      hour < item.hour + item.duration - 0.001
  );
  if (hasRealBooking) return { ok: false, blocked: false };

  const hasAnyPrebook = preBookedSlots.some(
    (slot) => slot.fieldId === fieldId && slot.date === date && Math.abs(slot.hour - hour) < 0.001 && slot.isBooked
  );
  if (hasAnyPrebook) return { ok: false, blocked: false };

  preBookedSlots.push({
    fieldId,
    date,
    hour,
    isBooked: true,
    bookedBy: OWNER_BLOCK_LABEL,
  });
  return { ok: true, blocked: true };
}
